import type { ComponentPropsWithRef } from 'react';
import { forwardRef } from 'react';
// Types
import type { ImageProps } from 'next/image';
import Image from 'next/image';

import clsx from 'clsx';

// Components
import Icon from '@/components/base/Icon';

type RadioImageProps = Omit<ComponentPropsWithRef<'input'>, 'src'> &
    Pick<ImageProps, 'src'> & {
        label?: string;
        subLabel?: string;
        withImgBackground?: boolean;
    };

const RadioImage = forwardRef<HTMLInputElement, RadioImageProps>(
    (
        { src, label, subLabel, id, checked, withImgBackground = true, className, ...restProps },
        ref
    ) => (
        <label
            htmlFor={id}
            className={clsx(
                'relative flex cursor-pointer flex-row items-center space-x-2.5 rounded-md border border-icoGray-300 bg-white p-3 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite',
                {
                    'outline outline-2 -outline-offset-2 outline-icoBlue-200 dark:outline-icoBlue':
                        checked,
                    'justify-center': !withImgBackground
                },
                className
            )}>
            <span
                className={clsx({
                    'rounded-md bg-icoBlue-100 p-2.5 dark:bg-icoBlue/20': withImgBackground
                })}>
                <Image alt="" src={src} width={18} height={18} />
            </span>
            <span className={clsx('flex flex-col space-y-0.5', { 'flex-1': withImgBackground })}>
                {label ? <span className="text-sm">{label}</span> : null}
                {subLabel ? (
                    <span className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {subLabel}
                    </span>
                ) : null}
            </span>
            {checked ? (
                <span className="absolute -right-2.5 -top-2.5 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-icoBlue-200 dark:border-icoDarkMode-anthracite dark:bg-icoBlue">
                    <Icon icon="check" className="text-xs text-icoBlue dark:text-white" />
                </span>
            ) : null}
            <input
                ref={ref}
                id={id}
                type="radio"
                className="hidden"
                checked={checked}
                {...restProps}
            />
        </label>
    )
);

RadioImage.displayName = 'RadioImage';

export default RadioImage;
